// Detects when a newer FinPlay build has been deployed while the user has the
// app open, and surfaces a non-disruptive toast asking them to reload. This
// REPLACES the previous behavior of silently auto-reloading the page, which
// could feel like "my trade just vanished" mid-session.
//
// How it works:
// 1. On boot, we record the current BUILD_ID (injected by vite.config.ts).
// 2. Every POLL_INTERVAL_MS we fetch "/" with cache:no-store and parse the
//    <meta name="finplay-build"> tag from the fresh HTML.
// 3. If the remote build id differs from the boot build id, we fire a
//    sonner toast with a "Reload" action. The user controls the reload.
//
// User data (portfolio, trades, league cash) lives in Supabase, so a reload
// can never lose it. This toast just keeps the UI in sync with new deploys.

import { toast } from "sonner";
import { BUILD_ID } from "@/lib/buildInfo";

const POLL_INTERVAL_MS = 90_000; // 90s — gentle, doesn't hammer the network
const MIN_FIRST_CHECK_MS = 2_000;
const STARTUP_RECOVERY_WINDOW_MS = 15_000;
const RELOAD_GUARD_PREFIX = "finplay:build-recovery:";

let started = false;
let bootSignature: string | null = null;
let notifiedSignature: string | null = null;
let startedAt = 0;

// In production, Vite injects hashed asset filenames into index.html, e.g.
// /assets/index-aB12cD34.js. When a new build deploys, the hash changes, so
// the entry script src is a reliable per-build fingerprint. We also include
// the modulepreload links so dev (where filenames don't hash) is still stable.
const computeSignature = (html: string): string | null => {
  const buildMatch = html.match(
    /<meta[^>]+name=["']finplay-build["'][^>]+content=["']([^"']+)["']/i
  ) ?? html.match(
    /<meta[^>]+content=["']([^"']+)["'][^>]+name=["']finplay-build["']/i
  );
  if (buildMatch?.[1]) return `build:${buildMatch[1]}`;

  const scriptMatches = Array.from(
    html.matchAll(/<script[^>]+src=["']([^"']+)["']/gi)
  ).map((m) => m[1]);
  const linkMatches = Array.from(
    html.matchAll(/<link[^>]+rel=["']modulepreload["'][^>]+href=["']([^"']+)["']/gi)
  ).map((m) => m[1]);
  const all = [...scriptMatches, ...linkMatches].filter((s) => !s.startsWith("http"));
  if (all.length === 0) return null;
  return all.sort().join("|");
};

const getLoadedSignature = (): string | null => {
  if (BUILD_ID && BUILD_ID !== "unknown") return `build:${BUILD_ID}`;
  return computeSignature(document.documentElement.outerHTML);
};

const recoverStaleStartup = async (remoteSignature: string) => {
  const guardKey = `${RELOAD_GUARD_PREFIX}${remoteSignature}`;
  try {
    if (sessionStorage.getItem(guardKey) === "1") return false;
    sessionStorage.setItem(guardKey, "1");
  } catch {
    // Continue with the one-time recovery when storage is restricted.
  }

  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } catch {
    // CacheStorage cleanup is best-effort; the cache-busted navigation remains.
  }

  const url = new URL(window.location.href);
  url.searchParams.set("build", remoteSignature.replace(/^build:/, ""));
  window.location.replace(url.toString());
  return true;
};

const checkOnce = async () => {
  try {
    const url = `/?_v=${Date.now()}`;
    const res = await fetch(url, { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return;
    const html = await res.text();
    const remoteSignature = computeSignature(html);
    if (!remoteSignature) return;
    if (bootSignature === null) bootSignature = getLoadedSignature();
    if (bootSignature === null) return;
    if (remoteSignature === bootSignature) return;
    if (remoteSignature === notifiedSignature) return;

    notifiedSignature = remoteSignature;

    // A mismatch immediately after boot means stale HTML/assets were loaded.
    // Recover automatically before the user can begin editing or trading.
    if (Date.now() - startedAt <= STARTUP_RECOVERY_WINDOW_MS) {
      if (await recoverStaleStartup(remoteSignature)) return;
    }

    toast("A new version of FinPlay is available", {
      description: "Your portfolio and progress are safely saved.",
      duration: Infinity,
      action: {
        label: "Reload",
        onClick: () => window.location.reload(),
      },
    });
  } catch {
    // Network errors are non-fatal; we'll retry on the next interval.
  }
};

export const startVersionCheck = () => {
  if (started || typeof window === "undefined") return;
  started = true;
  startedAt = Date.now();
  bootSignature = getLoadedSignature();

  const schedule = () => {
    // Only poll while the tab is visible to avoid wasting requests.
    if (document.visibilityState === "visible") void checkOnce();
  };

  window.setTimeout(() => {
    schedule();
    window.setInterval(schedule, POLL_INTERVAL_MS);
  }, MIN_FIRST_CHECK_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") schedule();
  });
};
